import {
  MdRoundDashboard,
  MdRoundDownload,
  MdRoundHelp,
  MdRoundLock,
  MdRoundMerge,
  MdRoundSecurity,
  MdRoundSettings,
} from "@teamworksdev/icons";
import { lazy } from "react";
import type { SupportTool } from "./types";

// Lazy load components to avoid circular dependencies
const AddCertToCamp = lazy(() => import("./tools/AddCertToCampTool").then((m) => ({ default: m.AddCertToCamp })));
const CRDashboardTool = lazy(() => import("./tools/CRDashboardTool").then((m) => ({ default: m.CRDashboardTool })));
const FileMergerTool = lazy(() => import("./tools/FileMergerTool").then((m) => ({ default: m.FileMergerTool })));
const SftpConfigTool = lazy(() => import("./tools/SftpConfigTool").then((m) => ({ default: m.SftpConfigTool })));
const SsoConfigTool = lazy(() => import("./tools/SsoConfigTool").then((m) => ({ default: m.SsoConfigTool })));
const Support = lazy(() => import("./tools/SupportTool").then((m) => ({ default: m.Support })));
const SupportDownloads = lazy(() =>
  import("./tools/SupportDownloadsTool").then((m) => ({ default: m.SupportDownloads })),
);

const SUPPORT_TOOLS_BASE_URL = import.meta.env.VITE_SUPPORT_TOOLS_BASE_URL;

const ALL_SUPPORT_TOOLS: SupportTool[] = [
  {
    id: "add-cert-to-camp",
    name: "Add cert to camp",
    description: "Automate SSL certificate provisioning for CAMP sites with GoDaddy DNS and AWS ACM",
    icon: <MdRoundLock />,
    category: "compliance",
    path: "add-cert-to-camp",
    component: AddCertToCamp,
    healthEndpoint: SUPPORT_TOOLS_BASE_URL ? `${SUPPORT_TOOLS_BASE_URL}/health/camp-cert` : undefined,
  },
  {
    id: "cr-dashboard",
    name: "C&R Dashboard",
    description: "Monitor data integrations and SSO configuration for Compliance & Recruiting organizations",
    icon: <MdRoundDashboard />,
    category: "compliance",
    path: "cr-dashboard",
    component: CRDashboardTool,
    healthEndpoint: SUPPORT_TOOLS_BASE_URL ? `${SUPPORT_TOOLS_BASE_URL}/health/cr-dashboard` : undefined,
  },
  {
    id: "file-merger",
    name: "File merger",
    description: "Join two CSV or XLSX files using database-style operations (inner, left, right, outer joins)",
    icon: <MdRoundMerge />,
    category: "general",
    path: "file-merger",
    component: FileMergerTool,
    healthEndpoint: SUPPORT_TOOLS_BASE_URL ? `${SUPPORT_TOOLS_BASE_URL}/health/file-merger` : undefined,
  },
  {
    id: "sftp-config",
    name: "SFTP config",
    description: "Configure SFTP users and manage access for data imports",
    icon: <MdRoundSettings />,
    category: "compliance",
    path: "sftp-config",
    component: SftpConfigTool,
    healthEndpoint: SUPPORT_TOOLS_BASE_URL ? `${SUPPORT_TOOLS_BASE_URL}/health/sftp-config` : undefined,
  },
  {
    id: "sso-config",
    name: "SSO config",
    description: "Configure SSO/Shibboleth connections for authentication",
    icon: <MdRoundSecurity />,
    category: "compliance",
    path: "sso-config",
    component: SsoConfigTool,
    healthEndpoint: SUPPORT_TOOLS_BASE_URL ? `${SUPPORT_TOOLS_BASE_URL}/health/sso-config` : undefined,
  },
  {
    id: "support",
    name: "Get support",
    description: "Get help and documentation for using Support Tools",
    icon: <MdRoundHelp />,
    category: "support",
    path: "support",
    component: Support,
  },
  {
    id: "support-downloads",
    name: "Support downloads",
    description: "Access and download support resources and tools",
    icon: <MdRoundDownload />,
    category: "general",
    path: "support-downloads",
    component: SupportDownloads,
  },
];

// Export all tools (no filtering needed in base)
export const SUPPORT_TOOLS: SupportTool[] = ALL_SUPPORT_TOOLS;

export const TOOLS_BY_CATEGORY = SUPPORT_TOOLS.reduce<Record<string, SupportTool[]>>((acc, tool) => {
  if (!acc[tool.category]) {
    acc[tool.category] = [];
  }
  acc[tool.category].push(tool);
  return acc;
}, {});

// Sort tools within each category alphabetically
Object.keys(TOOLS_BY_CATEGORY).forEach((category) => {
  TOOLS_BY_CATEGORY[category].sort((a, b) => a.name.localeCompare(b.name));
});

const CATEGORY_DISPLAY_NAMES: Record<string, string> = {
  support: "Support",
  compliance: "Compliance & Recruiting",
  general: "General",
};

export const getCategoryDisplayName = (category: string): string => {
  return CATEGORY_DISPLAY_NAMES[category] || category;
};
